// src/components/navigation/WebsiteNavBar.jsx
import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LangSwitcher from "../LangSwitcher";
import { GlassPanel } from "../GlassPanel";
import { CloseIcon } from "../icons/CloseIcon";
import { HamburgerIcon } from "../icons/HamburgerIcon";
import { LogoLink } from "../LogoLink";

/**
 * Public nav bar used on landing / privacy / terms.
 * Glass-panel style, brand name "pRR", and i18n text.
 * Mobile: hamburger opens a dropdown with nav links + CTA + language.
 */
function WebsiteNavBar({ className = "" }) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = useCallback(() => setMenuOpen((open) => !open), []);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 flex justify-center px-4 pt-3 pointer-events-none ${className}`}
    >
      <div className="w-full pointer-events-auto">
        <GlassPanel rounded="rounded-3xl md:rounded-full">
          {/* Top row: brand + desktop nav + mobile trigger */}
          <div className="flex items-center justify-between gap-4">
            {/* Logo */}
            <LogoLink />

            {/* Right side: desktop nav + lang + CTA / mobile hamburger */}
            <div className="flex items-center gap-3">
              {/* Desktop nav links */}
              <nav className="hidden md:flex items-center gap-4 text-xs md:text-sm">
                <a
                  href="/#how-it-works"
                  className="text-greyscale-2 hover:text-typography"
                >
                  {t("nav.links.howItWorks")}
                </a>

                <a
                  href="/#about"
                  className="text-greyscale-2 hover:text-typography"
                >
                  {t("nav.links.about")}
                </a>
              </nav>

              {/* Desktop: language + CTA */}
              <div className="hidden md:flex items-center gap-3">
                <LangSwitcher />
                <Link to="/recording/camera" className="primary-button-small">
                  {t("nav.cta")}
                </Link>
              </div>

              {/* Mobile: language icon + hamburger */}
              <div className="flex items-center gap-2 md:hidden">
                {/* Optional: small inline language icon (keeps switcher accessible) */}
                <LangSwitcher />

                <button
                  type="button"
                  onClick={toggleMenu}
                  aria-label={
                    menuOpen ? t("nav.aria.closeMenu") : t("nav.aria.openMenu")
                  }
                  aria-expanded={menuOpen}
                  className="
                    h-9 w-9 rounded-full  text-primary
                    flex items-center justify-center
                    
                  "
                >
                  {menuOpen ? <CloseIcon /> : <HamburgerIcon />}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile dropdown (below top row) */}
          {menuOpen && (
            <div className="mt-3 md:hidden border-t border-greyscale-1/40 pt-3">
              <nav className="flex flex-col gap-2 text-sm">
                <a
                  href="/#how-it-works"
                  className="text-greyscale-2 hover:text-typography py-1"
                  onClick={closeMenu}
                >
                  {t("nav.links.howItWorks")}
                </a>

                <a
                  href="/#about"
                  className="text-greyscale-2 hover:text-typography py-1"
                  onClick={closeMenu}
                >
                  {t("nav.links.about")}
                </a>
              </nav>

              <div className="mt-3 flex flex-col gap-2">
                {/* CTA takes full width on mobile */}
                <Link
                  to="/recording/camera"
                  className="primary-button-small w-full text-center"
                  onClick={closeMenu}
                >
                  {t("nav.cta")}
                </Link>
              </div>
            </div>
          )}
        </GlassPanel>
      </div>
    </header>
  );
}

export default React.memo(WebsiteNavBar);
